import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ListLayout } from "ui-components/Layout";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { MessageDialog } from "ui-components/Alert";
import { StyledDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { getApiSettingList, updateApiSetting } from "services/data.internal.service";

function AddApiSettingList() {
  const location = useLocation();
  const { customerCode, apiSecretKey } = location.state || {};
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isEdited, setIsEdited] = useState(false);
  const [dialog, setDialog] = useState(null);

  const isEditable = (row, field) => {
    const sys = (row.system || "").toUpperCase();
    const mod = (row.module || "").toUpperCase();
    const mp = (row.marketPlace || "").toUpperCase();
    if (sys === "IWEB") {
      if (mp === "DELYVA") return ["secretKey", "delyvaCustomerId", "delyvaCompanyId"].includes(field);
      if (mp === "ETRAX") return ["eTraxSourceId", "eTraxSecretKey"].includes(field);
    } else if (sys === "OMS") {
      return ["apiUrl"].includes(field);
    }
    return false;
  };


  const defaultRows = [
    {
      id: "1",
      settingId: null,
      customerCode,
      system: "iWeb",
      module: "SyncInventory",
      marketPlace: "IWEB",
      apiUrl: "https://iweb.sntglobal.com",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "2",
      settingId: null,
      customerCode,
      system: "iWeb",
      module: "GetAWB",
      marketPlace: "IWEB",
      apiUrl: "https://iweb.sntglobal.com/api/ehub/getawb",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "3",
      settingId: null,
      customerCode,
      system: "iWeb",
      module: "GetAWB",
      marketPlace: "DELYVA",
      apiUrl: "https://api.delyva.app",
      secretKey: "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "4",
      settingId: null,
      customerCode,
      system: "iWeb",
      module: "GetAWB",
      marketPlace: "ETRAX",
      apiUrl: "http://ets.sntglobal.com/rest/delivery.php?api=create",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "5",
      settingId: null,
      customerCode,
      system: "OMS",
      module: "SyncInventory",
      marketPlace: "EHUB",
      apiUrl: "",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "6",
      settingId: null,
      customerCode,
      system: "OMS",
      module: "GetAWB",
      marketPlace: "EHUB",
      apiUrl: "",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "7",
      settingId: null,
      customerCode,
      system: "OMS",
      module: "UpdateAWB",
      marketPlace: "EHUB",
      apiUrl: "",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "8",
      settingId: null,
      customerCode,
      system: "OMS",
      module: "UpdateReceivedItems",
      marketPlace: "EHUB",
      apiUrl: "",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
    {
      id: "9",
      settingId: null,
      customerCode,
      system: "OMS",
      module: "UpdateShippedItems",
      marketPlace: "EHUB",
      apiUrl: "",
      secretKey: apiSecretKey || "",
      eTraxSourceId: "",
      eTraxSecretKey: "",
      delyvaCustomerId: "",
      delyvaCompanyId: "",
      status: "Inactive",
    },
  ];

  const { isLoading } = useQuery({
    queryKey: ["api-setting", customerCode],
    queryFn: () => getApiSettingList(customerCode),
    onSuccess: (res) => {
      const data = res?.data?.data || [];

      if (data.length === 0) {
        setRows(defaultRows);
        return;
      }

      const formatted = data.map((d) => ({
        id: d.settingId.toString(),
        settingId: d.settingId,
        customerCode: d.customerCode,
        system: d.system ?? "",
        module: d.module ?? "",
        marketPlace: d.marketPlace ?? "",
        apiUrl: d.apiUrl ?? "",
        secretKey: d.secretKey ?? "",
        eTraxSourceId: d.eTraxSourceId ?? "",
        eTraxSecretKey: d.eTraxSecretKey ?? "",
        delyvaCustomerId: d.delyvaCustomerId ?? "",
        delyvaCompanyId: d.delyvaCompanyId ?? "",
        status: d.status === "1" ? "Active" : "Inactive",
      }));
      const formatDefault = defaultRows.map(x => {
        const matchRow = formatted.find(y => y.system === x.system && y.module === x.module && y.marketPlace === x.marketPlace)
        if (Boolean(matchRow)) {
          matchRow.id = x.id;
          return matchRow;
        }
        else return x;
      })

      setRows(formatDefault);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => updateApiSetting(payload),
    onSuccess: (data) => {
      setIsEdited(false);
      navigate("/setup/customer", { state: data.data.message });
      // setDialog(<MessageDialog title="Saved Successfully"
      //   onOk={() => {
      //     setDialog(null);
      //     navigate("/setup/customer");
      //   }} onClose={() => setDialog(null)} />);
    },
  });

  const handleProcessRowUpdate = (newRow) => {
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
    setIsEdited(true);
    return newRow;
  };

  const handleSave = () => {
    const errors = [];
    const activeRows = rows.filter((r) => r.status === "Active");

    //Validate Delyva/Etrax Conflict
    const hasActiveDelyva = activeRows.some((r) => r.marketPlace?.toUpperCase() === "DELYVA");
    const hasActiveEtrax = activeRows.some((r) => r.marketPlace?.toUpperCase() === "ETRAX");
    if (hasActiveDelyva && hasActiveEtrax) {
      errors.push("Only One Of Delyva or ETrax Can Be Active At The Same Time.");
    }

    // Validate IWEB/EHUB Conflict
    // const hasActiveIweb = activeRows.some((r) => r.marketPlace?.toUpperCase() === "IWEB");
    // const hasActiveEhub = activeRows.some((r) => r.marketPlace?.toUpperCase() === "EHUB");
    // if (hasActiveIweb && hasActiveEhub) {
    //   errors.push("Cannot Have Both IWeb & EHub Active At The Same Time.");
    // }

    //Validate GetAWB between IWEB and EHUB
    const hasActiveIwebGetAWB = activeRows.some((r) => r.module === "GetAWB" && r.marketPlace.toUpperCase() === "IWEB");
    const hasActiveEhubGetAWB = activeRows.some((r) => r.module === "GetAWB" && r.marketPlace.toUpperCase() === "EHUB");
    if (hasActiveIwebGetAWB && hasActiveEhubGetAWB) {
      errors.push("Cannot Have Both IWeb & EHub GetAWB Active At The Same Time.");
    }

    //Validate SyncInventory between IWEB and EHUB
    const hasActiveIwebSyncInventory = activeRows.some((r) => r.module === "SyncInventory" && r.marketPlace.toUpperCase() === "IWEB");
    const hasActiveEhubSyncInventory = activeRows.some((r) => r.module === "SyncInventory" && r.marketPlace.toUpperCase() === "EHUB");
    if (hasActiveIwebSyncInventory && hasActiveEhubSyncInventory) {
      errors.push("Cannot Have Both IWeb & EHub SyncInventory Active At The Same Time.");
    }

    //Validate Empty Required Fields Cannot Be Active
    activeRows.forEach((r) => {
      const emptyFields = [];
      if (r.marketPlace.toUpperCase() === "DELYVA") {
        if (!Boolean(r.secretKey)) emptyFields.push("secretKey");
        if (!Boolean(r.delyvaCustomerId)) emptyFields.push("delyvaCustomerId");
        if (!Boolean(r.delyvaCompanyId)) emptyFields.push("delyvaCompanyId");
      }
      else if (r.marketPlace.toUpperCase() === "ETRAX") {
        if (!Boolean(r.eTraxSourceId)) emptyFields.push("eTraxSourceId");
        if (!Boolean(r.eTraxSecretKey)) emptyFields.push("eTraxSecretKey");
      }
      else if (r.system === "OMS") {
        if (!Boolean(r.apiUrl)) emptyFields.push("apiUrl");
      }
      if (emptyFields.length > 0) {
        errors.push(`Row "${r.system} - ${r.module}" With Marketplace "${r.marketPlace}" Cannot Be Active Because Of Empty Fields: ${emptyFields.join(", ")}.`);
      }
    });

    if (errors.length > 0) {
      setDialog(
        <MessageDialog
          title="Validation Error" content={
            <div>
              {errors.map((e, i) => (<div key={i}>&gt; {e}</div>))}
            </div>
          }
          onOk={() => setDialog(null)} onClose={() => setDialog(null)}
        />
      ); return;
    }

    const payload = activeRows.map((row) => ({
      settingId: row.settingId,
      customerCode: row.customerCode,
      system: row.system,
      module: row.module,
      marketPlace: row.marketPlace,
      apiUrl: row.apiUrl,
      secretKey: row.secretKey,
      eTraxSourceId: row.eTraxSourceId,
      eTraxSecretKey: row.eTraxSecretKey,
      delyvaCustomerId: row.delyvaCustomerId,
      delyvaCompanyId: row.delyvaCompanyId,
      status: row.status === "Active" ? "1" : "0",
    }));

    updateMutation.mutate(payload);
  };

  // const handleAddRow = () => {
  //   const newRow = {
  //     id: `new-${Date.now()}`,
  //     settingId: null,
  //     customerCode: customerCode || "",
  //     system: "",
  //     module: "",
  //     marketPlace: "",
  //     apiUrl: "",
  //     secretKey: "",
  //     eTraxSourceId: "",
  //     eTraxSecretKey: "",
  //     delyvaCustomerId: "",
  //     delyvaCompanyId: "",
  //     status: "Inactive",
  //   };
  //   setRows((prev) => [...prev, newRow]);
  //   setIsEdited(true);
  // };

  const columns = [
    { field: "system", headerName: "System", minWidth: 125, },
    { field: "module", headerName: "Module", minWidth: 200, },
    { field: "marketPlace", headerName: "MarketPlace", minWidth: 125, },
    { field: "apiUrl", headerName: "API Url", minWidth: 525, editable: true },
    { field: "secretKey", headerName: "Secret Key", minWidth: 200, editable: true },
    { field: "eTraxSourceId", headerName: "eTrax SourceID", minWidth: 200, editable: true },
    { field: "eTraxSecretKey", headerName: "eTrax SecretKey", minWidth: 200, editable: true },
    { field: "delyvaCustomerId", headerName: "Delyva CustomerID", minWidth: 200, editable: true },
    { field: "delyvaCompanyId", headerName: "Delyva CompanyID", minWidth: 200, editable: true },
    { field: "status", headerName: "Status", minWidth: 125, type: "singleSelect", valueOptions: ["Active", "Inactive"], editable: true }
  ];

  return (
    <ListLayout lg={12}>
      {dialog}
      <MDBox sx={{ backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", p: 3, mt: 3 }}>
        <MDBox display="flex" flexDirection="column" mb={2}>
          <h2 style={{ color: "#1A73E8", marginBottom: 8 }}>API Setting</h2>
          <div>Customer Code: {customerCode}</div>
        </MDBox>

        <MDBox my={3}>
          <StyledDataGrid
            autoHeight
            rows={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={() => { }}
            isCellEditable={(params) => {
              const { row, field } = params;
              if (field === "status") return true;
              return isEditable(row, field);
            }}
            getCellClassName={(params) =>
              isEditable(params.row, params.field) || params.field === "status"
                ? "editable-cell"
                : ""
            }
            disableRowSelectionOnClick
          />
        </MDBox>

        <MDBox display="flex" justifyContent="flex-start" mb={1} gap={4}>
          {/* <MDButton color="success" onClick={handleAddRow}>
            Add
          </MDButton> */}
          <MDButton color="info" onClick={handleSave}>
            Save
          </MDButton>
        </MDBox>
      </MDBox>

      <style>
        {`
          .editable-cell {
            background-color: #3FC1FF;
          }
          .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader[data-field="apiUrl"],
          .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader[data-field="secretKey"],
          .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader[data-field="eTraxSourceId"],
          .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader[data-field="eTraxSecretKey"],
          .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader[data-field="delyvaCustomerId"],
          .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader[data-field="delyvaCompanyId"],
          .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader[data-field="status"] {
            background-color: #1A73E8;
            color: white;
          }
        `}
      </style>
    </ListLayout>
  );
}

export default AddApiSettingList;